import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Loader2, Banknote, QrCode, CreditCard, CircleDollarSign } from "lucide-react";

const TYPES = [
  { value: "dinheiro", label: "Dinheiro", icon: Banknote },
  { value: "pix", label: "PIX", icon: QrCode },
  { value: "debito", label: "Débito", icon: CreditCard },
  { value: "credito", label: "Crédito", icon: CreditCard },
  { value: "outro", label: "Outros", icon: CircleDollarSign },
];

export default function Pagamentos() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["admin", "gerente"].includes(user.role);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", type: "dinheiro", active: true });
  const [saving, setSaving] = useState(false);

  const { data: pms, isLoading } = useQuery({
    queryKey: ["payment-methods"],
    queryFn: async () => (await api.get("/payment-methods")).data,
  });

  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : { name: "", type: "dinheiro", active: true });
  }, [open, editing]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/payment-methods/${editing.id}`, form);
      else await api.post("/payment-methods", form);
      toast.success("Forma de pagamento salva");
      qc.invalidateQueries();
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/payment-methods/${p.id}`);
      toast.success("Removida");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="pagamentos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Formas de pagamento</h1>
          <p className="text-sm text-muted-foreground">Personalize as opções aceitas na recepção</p>
        </div>
        {canEdit && (
          <Button data-testid="new-pm-btn" onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus className="mr-1 h-4 w-4" /> Nova forma
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : (
        <div className="space-y-6" data-testid="pm-groups">
          {TYPES.map((t) => {
            const list = (pms || []).filter((p) => p.type === t.value);
            if (list.length === 0) return null;
            return (
              <div key={t.value}>
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </div>
                <div className="divide-y divide-border rounded-xl border border-border bg-card">
                  {list.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-3" data-testid={`pm-row-${p.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">{p.name}</span>
                        {!p.active && <span className="text-xs text-muted-foreground">(inativa)</span>}
                      </div>
                      {canEdit && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-pm-${p.id}`}
                            onClick={() => { setEditing(p); setOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500"
                            data-testid={`delete-pm-${p.id}`} onClick={() => remove(p)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="pm-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar forma de pagamento" : "Nova forma de pagamento"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="pm-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="PIX WhatsApp" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="pm-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <Label>Ativa</Label>
              <Switch data-testid="pm-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
            <Button data-testid="pm-save" onClick={save} disabled={saving} className="w-full h-11">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
